import type {
  LiveSession,
  MediaAsset,
  Playlist,
  ScheduleSlot,
  ServerMessage,
  Settings,
  StationStatus,
  StationUser,
  UserRole,
} from "@ees/shared";
import { authorizationValue, clearCredentials } from "./auth";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Only advertise a JSON body when there actually is one: Fastify rejects a
  // DELETE (or any request) that sets content-type: application/json with an
  // empty body — FST_ERR_CTP_EMPTY_JSON_BODY, 400 — which broke user removal.
  const hasBody = init?.body !== undefined && init.body !== "";
  const authorization = authorizationValue();
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(hasBody ? { "content-type": "application/json" } : {}),
      ...(authorization ? { authorization } : {}),
      ...(init?.headers ?? {}),
    },
  });
  // A 401 means the stored sign-in is absent or stale (the admin rotated the
  // password, or a wrong one slipped in). Drop it so the console can show the
  // sign-in form instead of failing every call with a raw status.
  if (response.status === 401) {
    clearCredentials();
    throw new Error(`Sign in required: ${init?.method ?? "GET"} ${path} was rejected (401).`);
  }
  if (!response.ok) {
    // Surface the server's reason (duplicate username, admin only, …) so the
    // console can say what actually went wrong instead of a bare status.
    let detail = String(response.status);
    try {
      const body = (await response.json()) as { error?: string };
      if (body?.error) detail = `${response.status} ${body.error}`;
    } catch {
      // Non-JSON error body; the status alone has to do.
    }
    throw new Error(`${init?.method ?? "GET"} ${path} failed: ${detail}`);
  }
  return (await response.json()) as T;
}

export const api = {
  status: () => request<StationStatus>("/api/status"),
  settings: () => request<Settings>("/api/settings"),
  saveSettings: (patch: Partial<Settings>) =>
    request<Settings>("/api/settings", { method: "PUT", body: JSON.stringify(patch) }),

  remoteStatus: () => request<RemoteSyncStatus>("/api/library/remote"),
  /** Trigger an iDrive sync in the background; poll remoteStatus for the result. */
  syncRemote: () => request<{ started: boolean }>("/api/library/sync", { method: "POST", body: "{}" }),

  media: (query: { kind?: string; search?: string; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (query.kind) params.set("kind", query.kind);
    if (query.search) params.set("search", query.search);
    if (query.limit) params.set("limit", String(query.limit));
    return request<{ total: number; items: MediaAsset[] }>(`/api/media?${params}`);
  },
  analyse: (mode: "pending" | "all") =>
    request<{ analysed: number }>("/api/library/analyze", {
      method: "POST",
      body: JSON.stringify({ mode }),
    }),
  scan: () => request<{ added: number }>("/api/library/scan", { method: "POST" }),

  playlists: () => request<Playlist[]>("/api/playlists"),
  createPlaylist: (input: { name: string; description?: string | null; loop?: boolean }) =>
    request<Playlist>("/api/playlists", { method: "POST", body: JSON.stringify(input) }),

  schedule: () =>
    request<{
      slots: ScheduleSlot[];
      shows: unknown[];
      upcoming: { slot: ScheduleSlot; startsAt: string; endsAt: string }[];
      timezone: string;
    }>("/api/schedule"),
  createSlot: (input: Record<string, unknown>) =>
    request<ScheduleSlot>("/api/schedule/slots", { method: "POST", body: JSON.stringify(input) }),
  updateSlot: (id: string, patch: Record<string, unknown>) =>
    request<ScheduleSlot>(`/api/schedule/slots/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  liveConfig: () =>
    request<{
      livekit: { enabled: boolean; url: string; room: string };
      rtmp: { enabled: boolean; publishUrl: string };
    }>("/api/live/config"),

  /** Join token for the shared LiveKit studio room (browser DJ publish). */
  liveToken: (hostName: string) =>
    request<{ token: string; url: string; room: string }>("/api/live/token", {
      method: "POST",
      body: JSON.stringify({ hostName }),
    }),

  /** Stable WHIP Server URL + stream key for OBS's output settings. */
  liveWhip: () =>
    request<{ url: string; streamKey: string; room: string }>("/api/live/whip", {
      method: "POST",
      body: "{}",
    }),

  /** Short-lived token for the browser-direct live input WebSocket. */
  liveInputToken: () =>
    request<{ token: string; expiresAt: number }>("/api/live/input-token", {
      method: "POST",
      body: "{}",
    }),

  /** Console identity: which account is calling and what it may manage. */
  consoleConfig: () =>
    request<{ stationName: string; timezone: string; user: { username: string | null; role: UserRole } }>(
      "/api/console/config",
    ),

  users: () => request<{ users: StationUser[] }>("/api/users"),
  createUser: (input: { username: string; password: string; role: UserRole }) =>
    request<StationUser>("/api/users", { method: "POST", body: JSON.stringify(input) }),
  deleteUser: (id: string) => request<{ ok: boolean }>(`/api/users/${id}`, { method: "DELETE" }),
};

/** Last completed library↔remote sync, as reported by /api/library/remote. */
export interface RemoteSyncStatus {
  enabled: boolean;
  rclone: boolean;
  running: boolean;
  lastStartedAt: number | null;
  lastFinishedAt: number | null;
  last: {
    ok: boolean;
    mode: "mirror" | "fetch-only";
    reason: string;
    durationMs: number;
    error: string | null;
  } | null;
}

export type StationListener = (message: ServerMessage) => void;

/**
 * Live status feed.
 *
 * Reconnects with a fixed backoff: the station pushes a status frame every
 * second, so a dropped socket is visible immediately and the player should not
 * wait long to resume.
 */
export function connectStation(onMessage: StationListener): () => void {
  let socket: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let timer: number | undefined;

  const open = () => {
    if (closed) return;
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    socket = new WebSocket(`${protocol}//${window.location.host}/ws`);

    socket.addEventListener("open", () => {
      attempt = 0;
    });

    socket.addEventListener("message", (event) => {
      try {
        onMessage(JSON.parse(String(event.data)) as ServerMessage);
      } catch {
        // Ignore malformed frames rather than tearing down the connection.
      }
    });

    socket.addEventListener("close", () => {
      if (closed) return;
      attempt += 1;
      timer = window.setTimeout(open, Math.min(1000 * 2 ** attempt, 15_000));
    });

    socket.addEventListener("error", () => socket?.close());
  };

  open();

  return () => {
    closed = true;
    if (timer) window.clearTimeout(timer);
    socket?.close();
  };
}

export function describeLive(session: LiveSession | null): string {
  if (!session) return "";
  const transport =
    session.transport === "rtmp" ? "RTMP" : session.transport === "browser" ? "Browser" : "WebRTC";
  return `${session.hostName} · ${transport}`;
}